<template>
	<div class="site-accordion" :style="containerStyle">
		<div
			v-for="(panel, idx) in panels"
			:key="panel.id"
			class="site-accordion__panel"
			:class="{ 'site-accordion__panel--open': isOpen(idx), 'site-accordion__panel--after-open': idx > 0 && isOpen(idx - 1) }"
			:style="panelStyle"
		>
			<!-- Panel header / trigger -->
			<button
				class="site-accordion__trigger"
				:style="triggerStyle"
				@click="toggle(idx)"
				:aria-expanded="isOpen(idx) ? 'true' : 'false'"
			>
				<i v-if="panel.icon" :class="'fa fa-' + panel.icon" class="site-accordion__panel-icon"></i>
				<span class="site-accordion__panel-title">{{ panel.title || 'Panel ' + (idx + 1) }}</span>
				<i
					class="fa site-accordion__chevron"
					:class="isOpen(idx) ? 'fa-chevron-up' : 'fa-chevron-down'"
					:style="isOpen(idx) ? { color: accentColor } : {}"
				></i>
			</button>

			<!-- Collapsible body with nested blocks -->
			<div
				class="site-accordion__body"
				:class="{ 'site-accordion__body--open': isOpen(idx) }"
			>
				<div class="site-accordion__body-inner" :style="bodyStyle">
					<site-block-renderer
						v-for="childBlock in (panel.blocks || [])"
						:key="childBlock.id"
						:block="childBlock"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
/**
 * SiteAccordionContainer.vue
 * ──────────────────────────
 * Public-facing read-only variant of AccordionContainerBlock for the
 * Website SPA. The editor variant
 * (Application/websites/cms/blocks/accordionContainerBlock/) statically
 * imports editor internals (blockRenderer, blockInserter, adminBlockWrapper,
 * vuedraggable) and commits editor mutations, so it must never ship in the
 * public bundle. This component mirrors its props and rendering behaviour
 * without any Vuex or editing support.
 *
 * Note: SiteBlockRenderer is registered globally in index.js to avoid the
 * circular dependency (renderer renders accordions, accordions render
 * child blocks via the renderer) — same pattern as SiteColumnsLayout.
 */
export default {
	name: 'SiteAccordionContainer',

	props: {
		/** Array of panel objects: { id, title, icon?, blocks[] } */
		panels: {
			type: Array,
			default: function () { return []; }
		},

		/** Open the first panel by default */
		defaultOpen: { type: [Boolean, String], default: true },

		/** Allow multiple panels open simultaneously */
		multiExpand: { type: [Boolean, String], default: false },

		/* ── Colours ── */
		triggerBg:          { type: String, default: '#F4F4F5' },
		triggerColor:       { type: String, default: '#1E2031' },
		triggerBorderColor: { type: String, default: '#DDDEE0' },
		bodyBg:             { type: String, default: '#FFFFFF' },
		accentColor:        { type: String, default: '#FB6A00' },

		/* ── Layout ── */
		borderRadius: { type: String, default: '6px' },
		gap:          { type: String, default: '0.5rem' },
		bodyPadding:  { type: String, default: '1rem' },

		/** Optional stable key for persisting open/closed state in localStorage */
		storageKey: { type: String, default: '' }
	},

	data: function () {
		return {
			openIndex: -1,
			openSet: {}
		};
	},

	created: function () {
		var restored = this.restoreState();
		if (restored) return;

		if (this.isDefaultOpen && this.panels && this.panels.length) {
			if (this.isMultiExpand) {
				this.$set(this.openSet, 0, true);
			} else {
				this.openIndex = 0;
			}
		}
	},

	computed: {
		isDefaultOpen: function () { return this.defaultOpen === true || this.defaultOpen === 'true'; },
		isMultiExpand: function () { return this.multiExpand === true || this.multiExpand === 'true'; },

		/** Stable key for localStorage. Uses panel IDs for a stable fingerprint. */
		resolvedStorageKey: function () {
			if (this.storageKey) return 'cms_accc_' + this.storageKey;
			var ids = (this.panels || []).map(function (p) { return p.id || ''; }).join('_');
			return 'cms_accc_' + ids;
		},

		containerStyle: function () {
			return { display: 'flex', flexDirection: 'column', gap: '0', width: '100%' };
		},

		panelStyle: function () {
			return {
				border: '1px solid ' + this.triggerBorderColor,
				borderRadius: this.borderRadius,
				overflow: 'hidden'
			};
		},

		triggerStyle: function () {
			return { background: this.triggerBg, color: this.triggerColor };
		},

		bodyStyle: function () {
			return { background: this.bodyBg, padding: this.bodyPadding };
		}
	},

	methods: {
		toggle: function (idx) {
			if (this.isMultiExpand) {
				this.$set(this.openSet, idx, !this.openSet[idx]);
			} else {
				this.openIndex = this.openIndex === idx ? -1 : idx;
			}
			this.saveState();
		},

		isOpen: function (idx) {
			if (this.isMultiExpand) return !!this.openSet[idx];
			return this.openIndex === idx;
		},

		/** Persist open/closed state to localStorage */
		saveState: function () {
			try {
				var state;
				if (this.isMultiExpand) {
					state = { multi: true, openSet: this.openSet };
				} else {
					state = { multi: false, openIndex: this.openIndex };
				}
				localStorage.setItem(this.resolvedStorageKey, JSON.stringify(state));
			} catch (e) { /* storage full or unavailable */ }
		},

		/** Restore open/closed state from localStorage. Returns true if restored. */
		restoreState: function () {
			try {
				var raw = localStorage.getItem(this.resolvedStorageKey);
				if (!raw) return false;
				var state = JSON.parse(raw);
				if (state.multi && state.openSet) {
					for (var k in state.openSet) {
						this.$set(this.openSet, k, !!state.openSet[k]);
					}
					return true;
				} else if (!state.multi && typeof state.openIndex === 'number') {
					this.openIndex = state.openIndex;
					return true;
				}
			} catch (e) { /* corrupted or unavailable */ }
			return false;
		}
	}
};
</script>

<style scoped>
.site-accordion { width: 100%; }

.site-accordion__panel { border: 1px solid #DDDEE0; margin-top: 15px; transition: margin-top 0.28s ease; }
.site-accordion__panel:first-child { margin-top: 0; }
.site-accordion__panel--open { margin-top: 30px; }
.site-accordion__panel--open:first-child { margin-top: 0; }
.site-accordion__panel--after-open { margin-top: 30px; }

.site-accordion__trigger {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.9rem 1.25rem;
	border: none;
	cursor: pointer;
	font-size: 1em;
	font-weight: 600;
	text-align: left;
	transition: filter 0.15s;
}

.site-accordion__trigger:hover { filter: brightness(0.96); }

.site-accordion__panel-title { flex: 1; }

.site-accordion__chevron {
	font-size: 0.85em;
	flex-shrink: 0;
}

.site-accordion__panel-icon { flex-shrink: 0; }

/* Slide animation via max-height */
.site-accordion__body {
	max-height: 0;
	overflow: hidden;
	transition: max-height 0.28s ease;
}

.site-accordion__body--open { max-height: 5000px; }

.site-accordion__body-inner { padding: 1rem; }
</style>
